import { createContext, useContext, useState, useMemo } from 'react';

const CollaborationStateContext = createContext(null);

export const useCollaborationState = () => {
  const context = useContext(CollaborationStateContext);
  if (!context) {
    throw new Error('useCollaborationState must be used within CollaborationStateProvider');
  }
  return context;
};

export const CollaborationStateProvider = ({ children }) => {
  // Authentication
  const [isAuthenticated, setIsAuthenticated] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);

  // Selected text for collaboration
  const [selectedText, setSelectedText] = useState('');
  const [selectedRange, setSelectedRange] = useState(null);
  const [selectedFormattedElements, setSelectedFormattedElements] = useState([]);

  // ✅ NEW: Persistent highlight state (keeps selection visible even after focus change)
  const [persistentHighlight, setPersistentHighlight] = useState(null); // { startOffset, endOffset, text }

  const actions = useMemo(() => ({
    // Authentication
    setIsAuthenticated: (authenticated) => {
      setIsAuthenticated(authenticated);
    },

    setCurrentUser: (user) => {
      setCurrentUser(user);

      // Set global user context
      if (user) {
        window.currentUser = {
          name: `${user.firstName} ${user.lastName}`,
          isAuthenticated: true,
          isPublic: false,
          id: user.id
        };
      } else {
        window.currentUser = null;
      }
    },

    // Selected text management
    setSelectedText: (text) => {
      setSelectedText(text || '');
    },

    setSelectedRange: (range) => {
      setSelectedRange(range);
    },

    setSelectedFormattedElements: (elements) => {
      setSelectedFormattedElements(elements || []);
    },

    clearSelection: () => {
      setSelectedText('');
      setSelectedRange(null);
      setSelectedFormattedElements([]);
      setPersistentHighlight(null); // ✅ Clear persistent highlight
    },

    updateSelection: (text, range, formattedElements = []) => {
      setSelectedText(text || '');
      setSelectedRange(range);
      setSelectedFormattedElements(formattedElements);

      // ✅ NEW: Create persistent highlight data when selection is made
      if (range && text) {
        try {
          const highlightData = {
            startOffset: range.startOffset,
            endOffset: range.endOffset,
            text: text,
            timestamp: Date.now()
          };
          setPersistentHighlight(highlightData);

        } catch (e) {
          console.warn('Failed to create persistent highlight:', e);
        }
      } else {
        setPersistentHighlight(null);
      }
    },

    // ✅ NEW: Explicitly set/clear persistent highlight
    setPersistentHighlight: (highlightData) => {
      setPersistentHighlight(highlightData);
    },

    clearPersistentHighlight: () => {
      setPersistentHighlight(null);
    }
  }), []);

  const value = useMemo(() => ({
    state: {
      isAuthenticated,
      currentUser,
      selectedText,
      selectedRange,
      selectedFormattedElements,
      persistentHighlight // ✅ NEW: Expose persistent highlight state
    },
    actions
  }), [
    isAuthenticated, currentUser,
    selectedText, selectedRange, selectedFormattedElements, persistentHighlight,
    actions
  ]);

  return (
    <CollaborationStateContext.Provider value={value}>
      {children}
    </CollaborationStateContext.Provider>
  );
};
